/* eslint-disable @nx/enforce-module-boundaries */
import { Injectable } from '@angular/core';
import { SectorMode, SectorState, SectorStateReason, SectorType } from 'libs/domain/sector/enum-sectors';

/**
 * Servei per formatejar dades (p.e.: obtenir textos a partir d'estats)
 */
@Injectable({
  providedIn: 'root'
})
export class SectorUiService {

  getSectorIconStateName(state: SectorState, mode: SectorMode){
    if(!state || !mode){ return ("sector--no-irrig"); }
    if (mode === SectorMode.AUTOMATIC){
      switch(state) {
        case SectorState.WAIT_WATER:
        case SectorState.START_IRRIG:
          return ("sector-start-auto")
        case SectorState.IRRIG:
          return ("sector-irrigating-auto");
      }
    }
    else if (mode === SectorMode.MANUAL){
      switch(state) {
        case SectorState.WAIT_WATER:
        case SectorState.START_IRRIG:
          return ("sector-start-manual")
        case SectorState.IRRIG:
          return ("sector-irrigating-manual");
        case SectorState.NO_IRRIG:
          return ("sector-no-irrig-manual")
      }
    }
    switch(state) {
      case SectorState.NO_IRRIG:
        return ("sector-no-irrig")
      case SectorState.DISCONNECTED:
        return ("sector-no-communication");
      case SectorState.INCIDENCE:
        return ("sector-incidence");
      case SectorState.MALFUNCTION:
        return ("sector-malfunction");
      case SectorState.LEAK:
        return ("sector-leak");
      default:
        return ("sector-no-irrig");
    }
  }

  getSectorModeString(mode: SectorMode){
    if (!mode){ return (""); }
    switch(mode){
      case SectorMode.AUTOMATIC:
        return ("Automático");
      case SectorMode.MANUAL:
        return ("Manual");
      default:
        return ("");
    }
  }

  getSectorStateString(state: SectorState){
    if (!state){ return ("No riego"); }
    switch(state){
      case SectorState.NO_IRRIG:
        return ("No riego");
      case SectorState.WAIT_WATER:
      case SectorState.START_IRRIG:
        return ("Inicia riego");
      case SectorState.IRRIG:
        return ("Riego");
      case SectorState.DISCONNECTED:
        return ("No comunica");
      case SectorState.INCIDENCE:
        return ("Incidencia");
      case SectorState.MALFUNCTION:
        return ("Avería");
      case SectorState.LEAK:
        return ("Fuga");
      default:
        return ("No riego");
    }
  }

  getStateBadgeClass(label: string): string {
    const baseClass = 'flex items-center gap-1 px-2.5 py-2 rounded-md font-title text-xs font-normal cursor-pointer';
    switch (label) {
      case 'Riego':
        return `${baseClass} bg-blue-light-500`;
      case 'Inicia riego':
        return `${baseClass} bg-success-500`;
      case 'No comunica':
        return `${baseClass} bg-yellow-500`;
      case 'Incidencia':
        return `${baseClass} bg-warning-500`;
      case 'Avería':
        return `${baseClass} bg-error-500`;
      case 'Fuga':
        return `${baseClass} bg-warning-500`;
      case 'No riego':
        return `${baseClass} bg-gray-200 text-gray-700`;
      default:
        return `${baseClass} bg-gray-500`;
    }
  }

  getModeBadgeClass(mode: string): string {
    const baseClass = 'text-white flex items-center gap-1 px-2.5 py-2 rounded-md font-title text-xs font-normal cursor-pointer';
    switch (mode) {
      case 'AUTOMATIC':
        return `${baseClass} bg-brand-500`;
      case 'MANUAL':
        return `${baseClass} bg-manual-500`;
      default:
        return `${baseClass} bg-gray-500`;
    }
  }

  checkSectorTypeIrrigationOutlet(type: SectorType){
    if(!type) return false;
    switch(type){
      case SectorType.ISOLATED:
      case SectorType.SECTORIZED:
      case SectorType.SELF_SECTORIZED:
        return true;
      case SectorType.SECTOR:
        return false;
    }
  }

  getSectorUserIconName(userType?: 'OWNER' | 'LESSEE' | 'INVITED'){
    return userType === 'OWNER' ? 'owner' : 'tenant';
  }
  
  getSectorStateReasonText(stateReason: SectorStateReason){
    if(!stateReason) return "";
    
    switch(stateReason){
      case SectorStateReason.NONE:
        return "";
      case SectorStateReason.TEMPORARY_BY_CONTROL:
        return "Paro temporal por control o condicionante";
      case SectorStateReason.CONDITIONAL_BY_CONTROL_OR_FUNCTION:
        return "Paro condicional por control o condicionante";
      case SectorStateReason.LIMIT_HYDRANT:
        return "Límite de hidrante alcanzado";
      case SectorStateReason.LIMIT_NETWORK:
        return "Límite de tramo de tubería alcanzado";
      case SectorStateReason.LIMIT_ALLOCATION:
        return "Dotación consumida";
      case SectorStateReason.LIMIT_BALANCE:
        return "Usuario sin saldo disponible";
      case SectorStateReason.LIMIT_SCHEDULE:
        return "Fuera de horario de disponibilidad";
      case SectorStateReason.FLOW_CONFIGURATION:
        return "Contador compartido: sector sin caudal previsto";
      case SectorStateReason.HYDRANT_SECTORS:
        return "Límite de hidrante alcanzado";
      case SectorStateReason.SECTORS_SEGMENT:
        return "Límite de tramo de tubería alcanzado";
      case SectorStateReason.METER_FLOW:
        return "Límite de caudal máximo en contador alcanzado";
      case SectorStateReason.ELEMENT_FAILURE:
        return "Algún elemento impide el riego";
      case SectorStateReason.IRRIGATION_ALLOCATION_EXCEEDED:
        return "Dotación consumida";
      case SectorStateReason.SCHEDULE:
        return "Limitación horaria";
      case SectorStateReason.METER_IN_USE:
        return "El contador ya está en uso por otro sector";
      case SectorStateReason.SYSTEM_DEF_STOP:
        return "Sistema fuera de servicio";
      case SectorStateReason.SYSTEM_CONDITIONAL_STOP:
        return "Sistema en STOP";
      case SectorStateReason.OUT_OF_SERVICE_DEVICE:
        return "Fuera de servicio o paro definitivo";
      case SectorStateReason.SECTOR_OUT_OF_SERVICE:
        return "Fuera de servicio manual";
      case SectorStateReason.DEF_STOP_BY_CONTROL_OR_FUNCTION:
        return "Paro definitivo por control o condicionante";
    }
  }

}
